import React, { useContext } from 'react';
import { DashboardContext } from '../../../context/DashboardContext';
import { Building2, TrendingUp, TrendingDown } from 'lucide-react';
import { formatCurrency } from '../../../lib/utils';

export default function BankCollections() {
  const { dashboardData } = useContext(DashboardContext);
  const { loading, bankCollections = [] } = dashboardData;

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-100">
        <div className="animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-20 bg-gray-100 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!bankCollections || bankCollections.length === 0) {
    return (
      <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-100 text-center text-gray-500">
        No payment collections found for this period.
      </div>
    );
  }

  const totalCollection = bankCollections.reduce(
    (sum, item) => sum + (item.totalAmount ?? 0),
    0
  );

  const allCollections = bankCollections.map((item) => ({
    
    displayName: item.paymentAccountName || item.paymentMode || 'Unknown',

    accountHolder: `${item.transactionCount || 0} transactions`,
    amount: item.totalAmount ?? 0,

    method: item.paymentMode,

    trend: item.trend || 'Stable',
    percentageChange: item.percentageChange ?? 0,

    icon: Building2,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
  }));

  return (
    <div className="bg-white rounded-lg p-5 shadow-sm border border-gray-100">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-900">Payment Collections</h2>
        <TrendingUp className="w-5 h-5 text-green-500" />
      </div>

      {/* Total Amount */}
      <div className="bg-blue-50 rounded-lg p-4 mb-4 border border-blue-100">
      <p className="text-xs text-gray-600 font-medium mb-1">
        Total {dashboardData.rangeLabel}
      </p>

        <p className="text-3xl font-bold text-gray-900">
          {formatCurrency(totalCollection)}
        </p>
      </div>

      {/* Collections List */}
      <div className="space-y-3">
        {allCollections.map((collection, index) => {
          const percentage = totalCollection
            ? ((collection.amount / totalCollection) * 100).toFixed(1)
            : 0;
          const Icon = collection.icon;
          const isRising = collection.trend.toLowerCase().includes('rise');
          const isFalling = collection.trend.toLowerCase().includes('fall');

          return (
            <div
              key={index}
              className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors duration-150"
            >
              {/* Top Row */}
              <div className="flex items-start justify-between mb-3">
                {/* Left - Icon and Name */}
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${collection.bgColor}`}>
                    <Icon className={`w-4 h-4 ${collection.color}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm">
                      {collection.displayName}
                    </h3>
                    <p className="text-xs text-gray-500">
                      {collection.accountHolder}
                    </p>
                  </div>
                </div>

                <div className="text-right">
                  <p className="text-lg font-bold text-gray-900">
                    {formatCurrency(collection.amount)}
                  </p>
                  <p className="text-xs font-semibold text-gray-500">
                     {collection.percentageChange}%
                  </p>
                </div>
              </div>

              {/* Bottom */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  {/* NEW — via always paymentMode */}
                  <span className="text-xs text-gray-600">
                    via {collection.method}
                  </span>
                  <span
                    className={`flex items-center gap-1 text-xs font-medium ${
                      isRising
                        ? 'text-green-600'
                        : isFalling
                        ? 'text-red-600'
                        : 'text-gray-600'
                    }`}
                  >
                    {isRising ? (
                      <TrendingUp className="w-3 h-3" />
                    ) : isFalling ? (
                      <TrendingDown className="w-3 h-3" />
                    ) : (
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-400"></div>
                    )}
                    {collection.trend}
                  </span>
                </div>

                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      isRising
                        ? 'bg-green-500'
                        : isFalling
                        ? 'bg-red-500'
                        : 'bg-blue-500'
                    }`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>

            </div>
          );
        })}
      </div>
    </div>
  );
}
